import { DEFAULT_FEE_SCHEDULE_CATEGORY_TYPE } from "_shared/fee-schedules/categories/types/fee-schedule-category-type";

/**
 * Reduces authorized ProposalJSON collection entries to the semantic values
 * permitted in the negotiation ledger. Display, permission, lifecycle, and
 * snapshot metadata never crosses this boundary.
 */
export function canonicalProposalCollectionItem(path: string, item: unknown): unknown {
  if (path.endsWith("TemplateIds")) return canonicalUuidValue(item);
  if (!isRecord(item)) return clone(item);

  if (path.endsWith("/documents")) {
    const canonical: Record<string, unknown> = {};
    const documentId =
      ownValue(item, "documentId") ??
      (isRecord(item.documentInfo) ? ownValue(item.documentInfo, "id") : undefined) ??
      ownValue(item, "id");
    if (documentId !== undefined) canonical.documentId = canonicalUuidValue(documentId);
    copyOwnUuidProperties(item, canonical, ["replacesId"]);
    return canonical;
  }

  if (path.endsWith("/members")) {
    const canonical: Record<string, unknown> = {};
    copyOwnUuidProperties(item, canonical, ["userId", "legalEntityId"]);
    return canonical;
  }

  if (path === "/feeSchedule/categories") {
    const canonical: Record<string, unknown> = {};
    copyOwnUuidProperties(item, canonical, ["id"]);
    copyOwnProperties(item, canonical, [
      "name",
      "description",
      "type",
      "feeRate",
      "feeCurrency",
      "feeUnit",
      "teamMember",
    ]);
    categoryWithoutTypeIsAFee(item, canonical);
    deferredFeeWithoutCurrencyOrUnit(item, canonical);
    return canonical;
  }

  return clone(item);
}

/**
 * A whole category recorded before the type existed carries none, and is a fee: the migration made
 * every stored category one, and `ProposalCollectionValuePolicy.Sanitize` fills it in the same way on
 * the server (#6864). Without this an old event and the row it produced would read as two different
 * categories. A partial value that names other columns alone has no `id`, and keeps its shape.
 */
function categoryWithoutTypeIsAFee(source: Record<string, unknown>, canonical: Record<string, unknown>): void {
  if (!Object.prototype.hasOwnProperty.call(source, "id")) return;
  if (canonical.type !== undefined && canonical.type !== null) return;
  canonical.type = DEFAULT_FEE_SCHEDULE_CATEGORY_TYPE;
}

/**
 * A deferred fee has no rate, and so no currency or unit either (#6627). The server can still hold
 * one beside the missing rate, while the editor writes the three as null. Read as they stand, the
 * two differ, and a save that edited a different category queued an update to this one. Only a
 * whole category with its rate present and null is changed here; a partial operation value that
 * names one fee column alone keeps it.
 */
function deferredFeeWithoutCurrencyOrUnit(source: Record<string, unknown>, canonical: Record<string, unknown>): void {
  if (!Object.prototype.hasOwnProperty.call(source, "feeRate") || source.feeRate !== null) return;
  for (const property of ["feeCurrency", "feeUnit"] as const) {
    if (Object.prototype.hasOwnProperty.call(canonical, property)) canonical[property] = null;
  }
}

function copyOwnUuidProperties(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  properties: readonly string[],
): void {
  for (const property of properties) {
    if (Object.prototype.hasOwnProperty.call(source, property) && source[property] !== undefined) {
      target[property] = canonicalUuidValue(source[property]);
    }
  }
}

function canonicalUuidValue(value: unknown): unknown {
  return getProposalCollectionItemIdentity(value) ?? clone(value);
}

/**
 * Produces the canonical collection value carried by a content command.
 *
 * Fee-category domain serialization deliberately writes optional editable
 * properties with `undefined` when a user clears them. JSON would otherwise
 * erase that intent, so command construction turns only those own properties
 * into explicit nulls. A genuinely omitted property remains omitted and is
 * still safe for sparse/older projections.
 *
 * The fee is one value written as three properties. A deferred fee sends all
 * three as null: sending only `feeUnit` left the server's merge keeping the old
 * rate and currency, so a revert to deferred was refused as a KeepPrevious that
 * contradicts its own result (#6628).
 */
export function canonicalProposalCollectionItemForCommand(path: string, item: unknown): unknown {
  const canonical = canonicalProposalCollectionItem(path, item);
  if (path !== "/feeSchedule/categories" || !isRecord(item) || !isRecord(canonical)) return canonical;

  for (const property of ["description", "feeUnit", "teamMember"] as const) {
    if (Object.prototype.hasOwnProperty.call(item, property) && item[property] === undefined) {
      canonical[property] = null;
    }
  }
  if (!isDeferredFeeCategory(item)) return canonical;

  for (const property of feeProperties) canonical[property] = null;
  return canonical;
}

const feeProperties = ["feeRate", "feeCurrency", "feeUnit"] as const;

/**
 * True when the category carries fee properties but no complete fee — the same rule
 * `FeeScheduleCategorySchema` decodes by: a fee needs both a rate and a currency. A category whose
 * currency is masked from this viewer has no fee to read, not a deferred one, so it is left alone.
 * Only a whole category is one: a leaf update's value (`{ feeRate: null }`) carries no `id`, and the
 * server refuses a leaf update that names more than one property.
 */
function isDeferredFeeCategory(item: Record<string, unknown>): boolean {
  if (!Object.prototype.hasOwnProperty.call(item, "id")) return false;
  if (item.currencyIsMasked === true) return false;
  if (!feeProperties.some((property) => Object.prototype.hasOwnProperty.call(item, property))) return false;
  return isNullish(item.feeRate) || isNullish(item.feeCurrency);
}

function isNullish(value: unknown): boolean {
  return value === null || value === undefined;
}

export function proposalSemanticJsonValuesEqual(left: unknown, right: unknown): boolean {
  if ((left === null || left === undefined) && (right === null || right === undefined)) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => proposalSemanticJsonValuesEqual(value, right[index]))
    );
  }
  if (isRecord(left) || isRecord(right)) {
    if (!isRecord(left) || !isRecord(right)) return false;
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    return [...keys].every((key) => proposalSemanticJsonValuesEqual(left[key], right[key]));
  }
  return Object.is(left, right);
}

/**
 * The strict sibling of `proposalSemanticJsonValuesEqual`: `null` and `undefined` are distinct,
 * everywhere, including nested. Deliberately not the same function with a flag — the two encode
 * different domain decisions and reusing one implementation for both keeps that decision explicit at
 * every call site rather than hidden behind a boolean.
 *
 * Correct for callers diffing a value against its own prior value to decide whether an edit is a net
 * no-op (`proposal-command-builder`'s content-operation diff, `proposal-collaboration-state`'s pending-
 * operation composition): most scalar fields distinguish "explicitly cleared" (`null`) from "never
 * set"/"round-tripped through an optional editor field" (`undefined`), and only a short, explicit list
 * of paths (`nullableAbsentFieldPaths` in `proposal-command-builder`) coalesces the two before this
 * function ever sees them. `proposalSemanticJsonValuesEqual` is instead correct where a queued value is
 * compared against server-supplied resolution/baseline state, which never distinguishes the two.
 */
export function proposalStrictJsonValuesEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => proposalStrictJsonValuesEqual(value, right[index]))
    );
  }
  if (isRecord(left) || isRecord(right)) {
    if (!isRecord(left) || !isRecord(right)) return false;
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    return [...keys].every((key) => proposalStrictJsonValuesEqual(left[key], right[key]));
  }
  return Object.is(left, right);
}

function copyOwnProperties(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  properties: readonly string[],
): void {
  for (const property of properties) {
    if (Object.prototype.hasOwnProperty.call(source, property) && source[property] !== undefined) {
      if (property === "teamMember" && isRecord(source[property])) {
        // Fee-category assignments carry member identity only. EntityMember serialization also
        // writes an optional replacesId as undefined, which is not JSON and is not accepted here.
        const member: Record<string, unknown> = {};
        copyOwnUuidProperties(source[property], member, ["userId", "legalEntityId"]);
        target[property] = member;
      } else {
        target[property] = clone(source[property]);
      }
    }
  }
}

function ownValue(source: Record<string, unknown>, property: string): unknown {
  return Object.prototype.hasOwnProperty.call(source, property) ? source[property] : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function clone<T>(value: T): T {
  return value === undefined ? value : structuredClone(value);
}
import { getProposalCollectionItemIdentity } from "work/proposals/negotiation/proposal-operation-identity";
